/**
 * Where the camera goes to show some routes: around their points and lines,
 * or — when that's a single point, as for a route's first point — centred on
 * it. Pure; the showRoutes action moves the map.
 */

export type LngLatBox = [[number, number], [number, number]];

interface RouteShape {
  markers: Array<{ markerGeometry: { coordinates: number[] } }>;
  lineStrings: Array<{ lineStringGeometry: { coordinates: number[][] } }>;
}

/** Zoom to centre a lone point at, unless the map is already closer in. */
export const SHOW_POINT_ZOOM = 13;

/** The box around the routes' points and lines, or null when they have none. */
export function boundsOfRoutes(routes: RouteShape[]): LngLatBox | null {
  let minLng = Infinity, minLat = Infinity, maxLng = -Infinity, maxLat = -Infinity;
  const extend = (coords: number[]) => {
    if (!coords || coords.length < 2 || !Number.isFinite(coords[0]) || !Number.isFinite(coords[1])) return;
    minLng = Math.min(minLng, coords[0]);
    maxLng = Math.max(maxLng, coords[0]);
    minLat = Math.min(minLat, coords[1]);
    maxLat = Math.max(maxLat, coords[1]);
  };
  for (const route of routes) {
    route.markers?.forEach(marker => extend(marker.markerGeometry?.coordinates));
    route.lineStrings?.forEach(line => line.lineStringGeometry?.coordinates?.forEach(extend));
  }
  return Number.isFinite(minLng) ? [[minLng, minLat], [maxLng, maxLat]] : null;
}

export type RouteCamera =
  | { kind: 'fit'; bounds: LngLatBox }
  | { kind: 'center'; center: [number, number] };

/** Fit the box, or centre on it when it's a single point (nothing to fit). */
export function cameraFor(box: LngLatBox): RouteCamera {
  const [[minLng, minLat], [maxLng, maxLat]] = box;
  if (maxLng - minLng < 1e-6 && maxLat - minLat < 1e-6) return { kind: 'center', center: [minLng, minLat] };
  return { kind: 'fit', bounds: box };
}
